import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import { X, Check, Plus, Trash2, Clock, RefreshCw, AlertTriangle } from 'lucide-react';
import { createTileLayer } from '../utils/mapTiles';

export default function ClientCardModal({
  client,
  isOpen,
  onClose,
  onSaveClient,
  clientContacts = [],
  onAddContact,
  onDeleteContact,
  rounds = [],
  clientDefaultTimeWindows = [],
  onAddClientTimeWindow,
  onUpdateClientTimeWindow,
  onDeleteClientTimeWindow,
  onReloadData
}) {
  if (!isOpen || !client) return null;

  const clientIdRef = useRef(client.id);
  const saveTimeoutRef = useRef(null);
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'

  const [formData, setFormData] = useState({
    id: client.id,
    name: client.name || '',
    technical_key: client.technical_key || '',
    code: client.code || '',
    address: client.address || '',
    postal_code: client.postal_code || '',
    city: client.city || '',
    country: client.country || 'Suisse',
    latitude: parseFloat(client.latitude) || 46.52,
    longitude: parseFloat(client.longitude) || 6.63,
    geocoding_precision: client.geocoding_precision || 'exact',
    phone: client.phone || '',
    email: client.email || '',
    roadbook: client.roadbook || '',
    driver_postit: client.driver_postit || '',
    default_service_time_mins: client.default_service_time_mins || 10,
    default_round_id: client.default_round_id || '',
    metadata: typeof client.metadata === 'object' ? JSON.stringify(client.metadata, null, 2) : (client.metadata || '{}')
  });

  const [activeSubTab, setActiveSubTab] = useState('general');
  const [newContact, setNewContact] = useState({ first_name: '', last_name: '', email: '', phone: '', is_primary: false });
  const [localContacts, setLocalContacts] = useState([]);

  // Agenda Time Windows State (00:00 to 24:00)
  const [selectedDayScope, setSelectedDayScope] = useState('jours_semaine');
  const [newTwStart, setNewTwStart] = useState('08:00');
  const [newTwEnd, setNewTwEnd] = useState('18:00');
  const [localTWs, setLocalTWs] = useState([]);
  const [resizing, setResizing] = useState(null);
  const agendaContainerRef = useRef(null);

  const DAYS = [
    { key: 'Lundi', label: 'Lundi' },
    { key: 'Mardi', label: 'Mardi' },
    { key: 'Mercredi', label: 'Mercredi' },
    { key: 'Jeudi', label: 'Jeudi' },
    { key: 'Vendredi', label: 'Vendredi' },
    { key: 'Samedi', label: 'Samedi' },
    { key: 'Dimanche', label: 'Dimanche' }
  ];

  const START_HOUR = 0;
  const END_HOUR = 24;
  const TOTAL_MINS = 24 * 60; // 1440 mins

  // Sync / initialize when client ID changes or on opening
  useEffect(() => {
    if (client && client.id) {
      const isNewClient = clientIdRef.current !== client.id;
      clientIdRef.current = client.id;

      if (isNewClient || !formData.id) {
        setFormData({
          id: client.id,
          name: client.name || '',
          technical_key: client.technical_key || '',
          code: client.code || '',
          address: client.address || '',
          postal_code: client.postal_code || '',
          city: client.city || '',
          country: client.country || 'Suisse',
          latitude: parseFloat(client.latitude) || 46.52,
          longitude: parseFloat(client.longitude) || 6.63,
          geocoding_precision: client.geocoding_precision || 'exact',
          phone: client.phone || '',
          email: client.email || '',
          roadbook: client.roadbook || '',
          driver_postit: client.driver_postit || '',
          default_service_time_mins: client.default_service_time_mins || 10,
          default_round_id: client.default_round_id || '',
          metadata: typeof client.metadata === 'object' ? JSON.stringify(client.metadata, null, 2) : (client.metadata || '{}')
        });
      }

      // Populate contacts: use props or fetch fresh
      const propContacts = (clientContacts || []).filter(c => c.client_id === client.id);
      if (propContacts.length > 0) {
        setLocalContacts(propContacts);
      } else {
        fetch(`/api/clients/${client.id}/contacts`)
          .then(res => res.ok ? res.json() : [])
          .then(data => {
            if (Array.isArray(data)) setLocalContacts(data);
          })
          .catch(() => {});
      }

      // Populate time windows: use props or fetch fresh
      const propTWs = (clientDefaultTimeWindows || []).filter(tw => tw.client_id === client.id);
      if (propTWs.length > 0) {
        setLocalTWs(propTWs);
      } else {
        fetch(`/api/clients/${client.id}/time-windows`)
          .then(res => res.ok ? res.json() : [])
          .then(data => {
            if (Array.isArray(data)) setLocalTWs(data);
          })
          .catch(() => {});
      }
    }
  }, [client?.id, isOpen]);

  // Handle ESC key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // IN-PLACE PERSISTENCE HANDLER
  const saveInPlace = async (stateToSave) => {
    const data = stateToSave || formData;
    if (!client?.id) return;

    setSaveStatus('saving');
    try {
      let metaJson = {};
      if (data.metadata) {
        if (typeof data.metadata === 'object') {
          metaJson = data.metadata;
        } else {
          try {
            metaJson = JSON.parse(data.metadata);
          } catch (e) {
            metaJson = { raw: data.metadata };
          }
        }
      }

      const payload = {
        name: data.name,
        code: data.code,
        technical_key: data.technical_key,
        phone: data.phone,
        email: data.email,
        roadbook: data.roadbook,
        driver_postit: data.driver_postit,
        default_service_time_mins: parseInt(data.default_service_time_mins) || 10,
        default_round_id: data.default_round_id ? parseInt(data.default_round_id) : null,
        address: data.address,
        street: data.address,
        postal_code: data.postal_code,
        city: data.city,
        country: data.country,
        latitude: parseFloat(data.latitude) || 46.52,
        longitude: parseFloat(data.longitude) || 6.63,
        geocoding_precision: data.geocoding_precision || 'exact',
        metadata: metaJson
      };

      const res = await fetch(`/api/clients/${client.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const updatedClient = await res.json();
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(null), 2000);
        if (onSaveClient) {
          onSaveClient(client.id, updatedClient);
        }
        if (onReloadData) {
          onReloadData();
        }
      } else {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus(null), 3500);
      }
    } catch (err) {
      console.error('Error saving client in-place:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3500);
    }
  };

  const handleFieldChange = (field, value, immediate = false) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

      if (immediate) {
        saveInPlace(updated);
      } else {
        saveTimeoutRef.current = setTimeout(() => {
          saveInPlace(updated);
        }, 500);
      }
      return updated;
    });
  };

  const timeStrToMins = (str) => {
    if (!str) return 480;
    const parts = str.split(':');
    return (parseInt(parts[0], 10) || 0) * 60 + (parseInt(parts[1], 10) || 0);
  };

  const minsToTimeStr = (mins) => {
    if (mins >= 1440) return '23:59:00';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
  };

  const formatDisplayTime = (str) => {
    if (!str) return '08:00';
    return str.slice(0, 5);
  };

  const handleAddQuickTimeWindow = async (e) => {
    if (e) e.preventDefault();
    if (!client?.id) return;

    let targetDays = [];
    if (selectedDayScope === 'jours_semaine') {
      targetDays = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'];
    } else if (selectedDayScope === 'jours_ouvrables') {
      targetDays = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    } else if (selectedDayScope === 'tous_les_jours') {
      targetDays = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
    } else {
      targetDays = [selectedDayScope];
    }

    const startVal = newTwStart.length === 5 ? `${newTwStart}:00` : newTwStart;
    const endVal = newTwEnd.length === 5 ? `${newTwEnd}:00` : newTwEnd;

    const tempRows = targetDays.map((d, idx) => ({
      id: `temp_${Date.now()}_${idx}`,
      client_id: client.id,
      day_of_week: d,
      days_of_week: d,
      time_window_start: startVal,
      time_window_end: endVal
    }));
    setLocalTWs(prev => [...prev, ...tempRows]);

    if (onAddClientTimeWindow) {
      await onAddClientTimeWindow(client.id, {
        day_of_week: selectedDayScope,
        time_window_start: startVal,
        time_window_end: endVal
      });
    } else {
      await fetch(`/api/clients/${client.id}/time-windows`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          day_of_week: selectedDayScope,
          time_window_start: startVal,
          time_window_end: endVal
        })
      });
      if (onReloadData) onReloadData();
    }
    // Refresh local time windows
    try {
      const res = await fetch(`/api/clients/${client.id}/time-windows`);
      if (res.ok) {
        const freshTWs = await res.json();
        setLocalTWs(freshTWs);
      }
    } catch (_) {}
  };

  const handleClearAllTWs = async () => {
    if (!window.confirm('Voulez-vous supprimer tous les créneaux horaires de ce client ?')) return;
    setLocalTWs([]);
    try {
      await fetch(`/api/clients/${client.id}/time-windows/all`, { method: 'DELETE' });
      if (onReloadData) onReloadData();
    } catch (err) {
      console.error(err);
    }
  };

  const handleColumnClick = async (dayKey, e) => {
    if (e.target.closest('.agenda-time-block') || e.target.closest('button')) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const colHeight = rect.height || 380;
    const clickedMins = (clickY / colHeight) * TOTAL_MINS;

    const snappedStartMins = Math.max(0, Math.min(TOTAL_MINS - 60, Math.round(clickedMins / 30) * 30));
    const snappedEndMins = Math.min(TOTAL_MINS, snappedStartMins + 120);

    const startStr = minsToTimeStr(snappedStartMins);
    const endStr = minsToTimeStr(snappedEndMins);

    const tempRow = {
      id: `temp_${Date.now()}`,
      client_id: client.id,
      day_of_week: dayKey,
      days_of_week: dayKey,
      time_window_start: startStr,
      time_window_end: endStr
    };
    setLocalTWs(prev => [...prev, tempRow]);

    if (onAddClientTimeWindow) {
      await onAddClientTimeWindow(client.id, {
        day_of_week: dayKey,
        time_window_start: startStr,
        time_window_end: endStr
      });
    } else {
      await fetch(`/api/clients/${client.id}/time-windows`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          day_of_week: dayKey,
          time_window_start: startStr,
          time_window_end: endStr
        })
      });
      if (onReloadData) onReloadData();
    }

    try {
      const res = await fetch(`/api/clients/${client.id}/time-windows`);
      if (res.ok) {
        const freshTWs = await res.json();
        setLocalTWs(freshTWs);
      }
    } catch (_) {}
  };

  const handleMouseDownResize = (e, tw, handle, dayKey) => {
    e.stopPropagation();
    e.preventDefault();

    const columnElem = e.currentTarget.closest('.agenda-day-column');
    const colHeight = columnElem ? columnElem.getBoundingClientRect().height : 380;

    setResizing({
      twId: tw.id,
      handle,
      day: dayKey,
      initialY: e.clientY,
      initialStartMins: timeStrToMins(tw.time_window_start),
      initialEndMins: timeStrToMins(tw.time_window_end),
      colHeight
    });
  };

  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e) => {
      const deltaY = e.clientY - resizing.initialY;
      const deltaMins = (deltaY / resizing.colHeight) * TOTAL_MINS;
      const snappedDeltaMins = Math.round(deltaMins / 15) * 15;

      setLocalTWs(prev => prev.map(tw => {
        if (tw.id !== resizing.twId) return tw;

        if (resizing.handle === 'top') {
          const newStartMins = Math.max(0, Math.min(resizing.initialEndMins - 15, resizing.initialStartMins + snappedDeltaMins));
          return {
            ...tw,
            time_window_start: minsToTimeStr(newStartMins)
          };
        } else {
          const newEndMins = Math.max(resizing.initialStartMins + 15, Math.min(TOTAL_MINS, resizing.initialEndMins + snappedDeltaMins));
          return {
            ...tw,
            time_window_end: minsToTimeStr(newEndMins)
          };
        }
      }));
    };

    const handleMouseUp = async () => {
      const targetTw = localTWs.find(t => t.id === resizing.twId);
      setResizing(null);

      if (targetTw && !String(targetTw.id).startsWith('temp_')) {
        if (onUpdateClientTimeWindow) {
          await onUpdateClientTimeWindow(targetTw.id, {
            time_window_start: targetTw.time_window_start,
            time_window_end: targetTw.time_window_end
          });
        } else {
          await fetch(`/api/clients/time-windows/${targetTw.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              time_window_start: targetTw.time_window_start,
              time_window_end: targetTw.time_window_end
            })
          });
          if (onReloadData) onReloadData();
        }
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing, localTWs, onUpdateClientTimeWindow]);

  const handleDeleteTW = async (e, twId) => {
    e.stopPropagation();
    setLocalTWs(prev => prev.filter(t => t.id !== twId));
    if (!String(twId).startsWith('temp_')) {
      if (onDeleteClientTimeWindow) {
        await onDeleteClientTimeWindow(twId);
      } else {
        await fetch(`/api/clients/time-windows/${twId}`, { method: 'DELETE' });
        if (onReloadData) onReloadData();
      }
    }
  };

  const handleAddContactSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!newContact.first_name && !newContact.last_name) return;
    const contactToAdd = { ...newContact };
    const tempContact = {
      id: `temp_contact_${Date.now()}`,
      client_id: client.id,
      ...contactToAdd
    };
    setLocalContacts(prev => [...prev, tempContact]);
    setNewContact({ first_name: '', last_name: '', email: '', phone: '', is_primary: false });

    if (onAddContact) {
      await onAddContact(client.id, contactToAdd);
    } else {
      await fetch(`/api/clients/${client.id}/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactToAdd)
      });
      if (onReloadData) onReloadData();
    }

    try {
      const res = await fetch(`/api/clients/${client.id}/contacts`);
      if (res.ok) {
        const freshContacts = await res.json();
        setLocalContacts(freshContacts);
      }
    } catch (_) {}
  };

  const handleDeleteContactItem = async (contactId) => {
    setLocalContacts(prev => prev.filter(c => c.id !== contactId));
    if (!String(contactId).startsWith('temp_')) {
      if (onDeleteContact) {
        await onDeleteContact(contactId);
      } else {
        await fetch(`/api/contacts/${contactId}`, { method: 'DELETE' });
        if (onReloadData) onReloadData();
      }
    }
  };

  const mapRef = useRef(null);
  const leafletMap = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current || activeSubTab !== 'address') return;

    const initialLat = parseFloat(formData.latitude) || 46.52;
    const initialLng = parseFloat(formData.longitude) || 6.63;

    if (!leafletMap.current) {
      leafletMap.current = L.map(mapRef.current, {
        center: [initialLat, initialLng],
        zoom: 14,
        zoomControl: true
      });

      createTileLayer('pale').addTo(leafletMap.current);
    }

    const map = leafletMap.current;

    if (markerRef.current) {
      map.removeLayer(markerRef.current);
    }

    let pinColor = '#22c55e';
    if (formData.geocoding_precision === 'approximate') pinColor = '#f59e0b';
    if (formData.geocoding_precision === 'wrong') pinColor = '#ef4444';

    const customIcon = L.divIcon({
      className: 'draggable-client-marker',
      html: `<div style="background:${pinColor}; color:#fff; border:2px solid #fff; border-radius:50%; width:32px; height:32px; display:flex; align-items:center; justify-content:center; font-size:16px; box-shadow:0 4px 10px rgba(0,0,0,0.5); cursor:grab;">📍</div>`,
      iconSize: [32, 32],
      iconAnchor: [16, 16]
    });

    const marker = L.marker([initialLat, initialLng], {
      icon: customIcon,
      draggable: true
    }).addTo(map);

    marker.on('dragend', (e) => {
      const latLng = e.target.getLatLng();
      const newLat = parseFloat(latLng.lat.toFixed(6));
      const newLng = parseFloat(latLng.lng.toFixed(6));

      const updated = {
        ...formData,
        latitude: newLat,
        longitude: newLng,
        geocoding_precision: 'manual'
      };
      setFormData(updated);
      saveInPlace(updated);
    });

    markerRef.current = marker;
    map.setView([initialLat, initialLng], map.getZoom());

    setTimeout(() => {
      map.invalidateSize();
    }, 150);

  }, [formData.latitude, formData.longitude, formData.geocoding_precision, activeSubTab]);

  const statusColor = formData.geocoding_precision === 'approximate' ? '#f59e0b' : formData.geocoding_precision === 'wrong' ? '#ef4444' : '#22c55e';

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="overlay-card" style={{ width: '920px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: '0', overflow: 'hidden' }}>
        
        {/* Modal Top Header with Live In-Place Save Indicator */}
        <div style={{ background: '#1e293b', padding: '14px 20px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: statusColor, display: 'inline-block' }} title={`Précision: ${formData.geocoding_precision}`} />
            <h2 style={{ fontSize: '1.2rem', fontWeight: '700', color: '#f8fafc', margin: 0 }}>
              {formData.name || 'Fiche Client'}
            </h2>
            {formData.code && (
              <span style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '4px', padding: '1px 6px', fontSize: '0.72rem', color: '#94a3b8', fontFamily: 'monospace' }}>
                {formData.code}
              </span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            {/* Live Auto-Save Status Indicator */}
            {saveStatus === 'saving' && (
              <span style={{ fontSize: '0.74rem', color: '#38bdf8', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '600' }}>
                <RefreshCw size={13} className="animate-spin" /> Sauvegarde...
              </span>
            )}
            {saveStatus === 'saved' && (
              <span style={{ fontSize: '0.74rem', color: '#22c55e', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '600' }}>
                <Check size={14} /> Enregistré
              </span>
            )}
            {saveStatus === 'error' && (
              <span style={{ fontSize: '0.74rem', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '600' }}>
                <AlertTriangle size={14} /> Erreur sauvegarde
              </span>
            )}

            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px', borderRadius: '4px' }}
              title="Fermer (Échap)"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Modal Sub-Tabs */}
        <div style={{ display: 'flex', background: '#0f172a', borderBottom: '1px solid #334155', padding: '0 16px' }}>
          <button className={`db-subtab ${activeSubTab === 'general' ? 'active' : ''}`} onClick={() => setActiveSubTab('general')}>
            Informations
          </button>
          <button
            className={`db-subtab ${activeSubTab === 'address' ? 'active' : ''}`}
            onClick={() => setActiveSubTab('address')}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: statusColor, display: 'inline-block' }} />
            Adresse
          </button>
          <button className={`db-subtab ${activeSubTab === 'timewindows' ? 'active' : ''}`} onClick={() => setActiveSubTab('timewindows')}>
            Créneaux horaires ({localTWs.length})
          </button>
        </div>

        {/* Modal Main Body (No form wrapper, saves in-place) */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          
          {/* TAB 1: INFORMATIONS (General info, Contacts, Consignes & Post-it) */}
          {activeSubTab === 'general' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              
              {/* General Fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Nom Client / Raison Sociale</label>
                  <input
                    type="text"
                    className="search-input"
                    value={formData.name}
                    onChange={e => handleFieldChange('name', e.target.value)}
                    onBlur={() => handleFieldChange('name', formData.name, true)}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Téléphone</label>
                  <input
                    type="text"
                    className="search-input"
                    value={formData.phone}
                    onChange={e => handleFieldChange('phone', e.target.value)}
                    onBlur={() => handleFieldChange('phone', formData.phone, true)}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Email</label>
                  <input
                    type="email"
                    className="search-input"
                    value={formData.email}
                    onChange={e => handleFieldChange('email', e.target.value)}
                    onBlur={() => handleFieldChange('email', formData.email, true)}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Tournée par Défaut</label>
                  <select
                    className="search-input"
                    value={formData.default_round_id || ''}
                    onChange={e => handleFieldChange('default_round_id', e.target.value ? parseInt(e.target.value) : null, true)}
                  >
                    <option value="">Aucune (Non assigné)</option>
                    {rounds.map(r => (
                      <option key={r.id} value={r.id}>{r.name} ({r.code})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Temps de Service par Défaut (min)</label>
                  <input
                    type="number"
                    className="search-input"
                    value={formData.default_service_time_mins}
                    onChange={e => handleFieldChange('default_service_time_mins', parseInt(e.target.value) || 0)}
                    onBlur={() => handleFieldChange('default_service_time_mins', formData.default_service_time_mins, true)}
                  />
                </div>
              </div>

              {/* Contacts Section (Directly below general fields) */}
              <div style={{ borderTop: '1px solid #334155', paddingTop: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <label style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block' }}>Contacts ({localContacts.length})</label>

                {localContacts.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {localContacts.map(c => (
                      <div key={c.id} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '6px', padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          <div style={{ fontWeight: '600', color: '#f8fafc', fontSize: '0.82rem' }}>
                            {c.first_name} {c.last_name} {c.is_primary && <span style={{ color: '#22c55e', fontSize: '0.68rem', background: 'rgba(34, 197, 94, 0.15)', padding: '1px 5px', borderRadius: '4px', marginLeft: '6px' }}>Principal</span>}
                          </div>
                          <div style={{ fontSize: '0.74rem', color: '#94a3b8', display: 'flex', gap: '12px', marginTop: '2px' }}>
                            <span>📧 {c.email || '—'}</span>
                            <span>📞 {c.phone || '—'}</span>
                          </div>
                        </div>

                        <button type="button" className="btn-action" style={{ padding: '4px 6px', color: '#ef4444', borderColor: '#ef4444' }} onClick={() => handleDeleteContactItem(c.id)}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Contact Row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.2fr 1fr auto', gap: '8px', alignItems: 'center', background: '#0b1120', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px' }}>
                  <input type="text" placeholder="Prénom..." className="search-input" value={newContact.first_name} onChange={e => setNewContact({ ...newContact, first_name: e.target.value })} />
                  <input type="text" placeholder="Nom..." className="search-input" value={newContact.last_name} onChange={e => setNewContact({ ...newContact, last_name: e.target.value })} />
                  <input type="email" placeholder="Email..." className="search-input" value={newContact.email} onChange={e => setNewContact({ ...newContact, email: e.target.value })} />
                  <input type="text" placeholder="Tél..." className="search-input" value={newContact.phone} onChange={e => setNewContact({ ...newContact, phone: e.target.value })} />
                  <button type="button" className="btn-action" style={{ background: '#22c55e', color: '#0f172a', fontWeight: '700', border: 'none', padding: '6px 12px' }} onClick={handleAddContactSubmit}>
                    <Plus size={14} /> Ajouter
                  </button>
                </div>
              </div>

              {/* Consignes client, Post-it & Métadonnées (Directly below contacts) */}
              <div style={{ borderTop: '1px solid #334155', paddingTop: '14px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                    Consignes client
                  </label>
                  <textarea
                    className="search-input"
                    style={{ minHeight: '100px', lineHeight: '1.4' }}
                    placeholder="Instructions permanentes pour les livraisons..."
                    value={formData.roadbook}
                    onChange={e => handleFieldChange('roadbook', e.target.value)}
                    onBlur={() => handleFieldChange('roadbook', formData.roadbook, true)}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                    Post-it
                  </label>
                  <textarea
                    className="search-input"
                    style={{ minHeight: '100px', lineHeight: '1.4' }}
                    placeholder="Notes ponctuelles ou observations..."
                    value={formData.driver_postit}
                    onChange={e => handleFieldChange('driver_postit', e.target.value)}
                    onBlur={() => handleFieldChange('driver_postit', formData.driver_postit, true)}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                    Métadonnées (JSON)
                  </label>
                  <textarea
                    className="search-input"
                    style={{ minHeight: '100px', lineHeight: '1.4', fontFamily: 'monospace', fontSize: '0.75rem' }}
                    placeholder='{"key": "value"}'
                    value={formData.metadata}
                    onChange={e => handleFieldChange('metadata', e.target.value)}
                    onBlur={() => handleFieldChange('metadata', formData.metadata, true)}
                  />
                </div>
              </div>

            </div>
          )}

          {/* TAB 2: ADRESSE */}
          {activeSubTab === 'address' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              
              {/* Address Fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.5fr 1.2fr auto', gap: '10px', alignItems: 'flex-end' }}>
                <div>
                  <label style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Adresse Rue et Numéro</label>
                  <input
                    type="text"
                    className="search-input"
                    value={formData.address}
                    onChange={e => handleFieldChange('address', e.target.value)}
                    onBlur={() => handleFieldChange('address', formData.address, true)}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>NPA</label>
                  <input
                    type="text"
                    className="search-input"
                    value={formData.postal_code}
                    onChange={e => handleFieldChange('postal_code', e.target.value)}
                    onBlur={() => handleFieldChange('postal_code', formData.postal_code, true)}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Ville</label>
                  <input
                    type="text"
                    className="search-input"
                    value={formData.city}
                    onChange={e => handleFieldChange('city', e.target.value)}
                    onBlur={() => handleFieldChange('city', formData.city, true)}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Pays</label>
                  <input
                    type="text"
                    className="search-input"
                    value={formData.country}
                    onChange={e => handleFieldChange('country', e.target.value)}
                    onBlur={() => handleFieldChange('country', formData.country, true)}
                  />
                </div>

                <div>
                  <button
                    type="button"
                    title="Géocoder l'adresse"
                    disabled
                    style={{
                      background: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: '6px',
                      padding: '8px 10px',
                      color: '#64748b',
                      opacity: 0.6,
                      cursor: 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '1px'
                    }}
                  >
                    <RefreshCw size={15} />
                  </button>
                </div>
              </div>

              {/* Coordinates and Precision */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.5fr', gap: '10px' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Latitude</label>
                  <input
                    type="number"
                    step="0.000001"
                    className="search-input"
                    value={formData.latitude}
                    onChange={e => handleFieldChange('latitude', parseFloat(e.target.value))}
                    onBlur={() => handleFieldChange('latitude', formData.latitude, true)}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Longitude</label>
                  <input
                    type="number"
                    step="0.000001"
                    className="search-input"
                    value={formData.longitude}
                    onChange={e => handleFieldChange('longitude', parseFloat(e.target.value))}
                    onBlur={() => handleFieldChange('longitude', formData.longitude, true)}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Précision Géocodage</label>
                  <select
                    className="search-input"
                    value={formData.geocoding_precision}
                    onChange={e => handleFieldChange('geocoding_precision', e.target.value, true)}
                  >
                    <option value="exact">Exact / Certain (Vert)</option>
                    <option value="manual">Manual / Carte (Vert)</option>
                    <option value="approximate">Approximate (Orange)</option>
                    <option value="wrong">Wrong / Problème (Rouge)</option>
                  </select>
                </div>
              </div>

              {/* Leaflet Map */}
              <div style={{ height: '300px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #334155' }}>
                <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
              </div>
            </div>
          )}

          {/* TAB 3: CRÉNEAUX HORAIRES (VERTICAL WEEK AGENDA 00:00 - 24:00) */}
          {activeSubTab === 'timewindows' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {/* Quick Add Toolbar */}
              <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', padding: '10px 12px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 2.5fr 1fr 1fr auto', gap: '10px', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Clock size={16} color="#38bdf8" />
                  </div>
                  <div>
                    <select
                      className="search-input"
                      value={selectedDayScope}
                      onChange={e => setSelectedDayScope(e.target.value)}
                      style={{ background: '#0f172a', color: '#fff', fontSize: '0.8rem', border: '1px solid #334155' }}
                    >
                      <option value="jours_semaine">Jours de semaine (du lundi au vendredi)</option>
                      <option value="jours_ouvrables">Jours ouvrables (du lundi au samedi)</option>
                      <option value="tous_les_jours">Tous les jours (du lundi au dimanche)</option>
                      <option value="Lundi">Lundi uniquement</option>
                      <option value="Mardi">Mardi uniquement</option>
                      <option value="Mercredi">Mercredi uniquement</option>
                      <option value="Jeudi">Jeudi uniquement</option>
                      <option value="Vendredi">Vendredi uniquement</option>
                      <option value="Samedi">Samedi uniquement</option>
                      <option value="Dimanche">Dimanche uniquement</option>
                    </select>
                  </div>
                  <div>
                    <input
                      type="time"
                      className="search-input"
                      value={newTwStart}
                      onChange={e => setNewTwStart(e.target.value)}
                      style={{ background: '#0f172a', color: '#fff', fontSize: '0.8rem', border: '1px solid #334155' }}
                      title="Heure de début"
                    />
                  </div>
                  <div>
                    <input
                      type="time"
                      className="search-input"
                      value={newTwEnd}
                      onChange={e => setNewTwEnd(e.target.value)}
                      style={{ background: '#0f172a', color: '#fff', fontSize: '0.8rem', border: '1px solid #334155' }}
                      title="Heure de fin"
                    />
                  </div>
                  <button
                    type="button"
                    className="btn-action"
                    style={{ background: '#22c55e', color: '#0f172a', fontWeight: '700', border: 'none', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '6px' }}
                    onClick={handleAddQuickTimeWindow}
                  >
                    <Plus size={15} /> Ajouter
                  </button>
                </div>
              </div>

              {/* 7-DAY VERTICAL WEEK AGENDA VIEW (00:00 to 24:00) */}
              <div
                ref={agendaContainerRef}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '46px repeat(7, 1fr)',
                  gap: '4px',
                  background: '#0b1120',
                  borderRadius: '8px',
                  border: '1px solid #334155',
                  padding: '8px',
                  overflow: 'hidden'
                }}
              >
                {/* Header Row: Time Column Title + 7 Days */}
                <div style={{ textAlign: 'center', fontSize: '0.7rem', color: '#64748b', fontWeight: '700', paddingBottom: '6px', borderBottom: '1px solid #1e293b' }}>
                  Heure
                </div>
                {DAYS.map(day => {
                  const count = localTWs.filter(tw => {
                    const d = tw.day_of_week || tw.days_of_week;
                    if (!d) return day.key === 'Lundi';
                    return d.toLowerCase().includes(day.key.toLowerCase());
                  }).length;

                  return (
                    <div
                      key={day.key}
                      style={{
                        textAlign: 'center',
                        fontSize: '0.74rem',
                        fontWeight: '700',
                        color: count > 0 ? '#38bdf8' : '#cbd5e1',
                        paddingBottom: '6px',
                        borderBottom: '1px solid #1e293b',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '2px'
                      }}
                    >
                      <span>{day.label}</span>
                      <span style={{ fontSize: '0.62rem', fontWeight: '500', color: count > 0 ? '#22c55e' : '#64748b' }}>
                        {count > 0 ? `${count} créneau${count > 1 ? 'x' : ''}` : 'Fermé'}
                      </span>
                    </div>
                  );
                })}

                {/* Time Ruler (Left Column: 00:00 to 24:00, labeled every 2 hours) */}
                <div style={{ position: 'relative', height: '380px' }}>
                  {Array.from({ length: 13 }).map((_, i) => {
                    const hour = i * 2;
                    const topPct = (hour / 24) * 100;
                    return (
                      <div
                        key={hour}
                        style={{
                          position: 'absolute',
                          top: `${topPct}%`,
                          left: 0,
                          right: 0,
                          transform: 'translateY(-50%)',
                          fontSize: '0.64rem',
                          color: '#64748b',
                          textAlign: 'right',
                          paddingRight: '6px',
                          fontWeight: '600',
                          userSelect: 'none'
                        }}
                      >
                        {String(hour).padStart(2, '0')}:00
                      </div>
                    );
                  })}
                </div>

                {/* 7 Vertical Day Columns */}
                {DAYS.map(day => {
                  const dayTWs = localTWs.filter(tw => {
                    const d = tw.day_of_week || tw.days_of_week;
                    if (!d) return day.key === 'Lundi';
                    return d.toLowerCase().includes(day.key.toLowerCase());
                  });

                  return (
                    <div
                      key={day.key}
                      className="agenda-day-column"
                      onClick={(e) => handleColumnClick(day.key, e)}
                      style={{
                        position: 'relative',
                        height: '380px',
                        background: 'rgba(30, 41, 59, 0.45)',
                        borderRadius: '4px',
                        border: '1px solid #1e293b',
                        cursor: 'crosshair',
                        overflow: 'hidden',
                        transition: 'background 0.15s ease'
                      }}
                      title={`Cliquer pour ajouter un créneau sur ${day.label}`}
                    >
                      {/* Hour guide lines every 2 hours */}
                      {Array.from({ length: 13 }).map((_, i) => (
                        <div
                          key={i}
                          style={{
                            position: 'absolute',
                            top: `${(i / 12) * 100}%`,
                            left: 0,
                            right: 0,
                            borderTop: '1px dashed rgba(51, 65, 85, 0.35)',
                            pointerEvents: 'none'
                          }}
                        />
                      ))}

                      {/* Render Time Blocks */}
                      {dayTWs.map(tw => {
                        const sMins = timeStrToMins(tw.time_window_start);
                        const eMins = timeStrToMins(tw.time_window_end);
                        const topPct = Math.max(0, Math.min(100, (sMins / TOTAL_MINS) * 100));
                        const heightPct = Math.max(6, Math.min(100 - topPct, ((eMins - sMins) / TOTAL_MINS) * 100));

                        return (
                          <div
                            key={tw.id}
                            className="agenda-time-block"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              position: 'absolute',
                              top: `${topPct}%`,
                              height: `${heightPct}%`,
                              left: '2px',
                              right: '2px',
                              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.28) 0%, rgba(5, 150, 105, 0.45) 100%)',
                              border: '1px solid #22c55e',
                              borderRadius: '4px',
                              padding: '2px 4px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                              zIndex: 10,
                              userSelect: 'none',
                              boxSizing: 'border-box'
                            }}
                          >
                            {/* Top resize handle */}
                            <div
                              onMouseDown={(e) => handleMouseDownResize(e, tw, 'top', day.key)}
                              style={{
                                height: '7px',
                                cursor: 'ns-resize',
                                margin: '-2px -4px 0 -4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                              title="Glisser pour modifier le début"
                            >
                              <div style={{ width: '14px', height: '2px', background: '#4ade80', borderRadius: '1px' }} />
                            </div>

                            {/* Block Content */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '2px', minWidth: 0, overflow: 'hidden' }}>
                              <span
                                style={{
                                  fontSize: '0.64rem',
                                  fontWeight: '800',
                                  color: '#ffffff',
                                  lineHeight: '1.1',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}
                              >
                                {formatDisplayTime(tw.time_window_start)} - {formatDisplayTime(tw.time_window_end)}
                              </span>
                              <button
                                type="button"
                                onClick={(e) => handleDeleteTW(e, tw.id)}
                                style={{
                                  background: 'rgba(239, 68, 68, 0.85)',
                                  border: 'none',
                                  borderRadius: '50%',
                                  width: '13px',
                                  height: '13px',
                                  color: '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  padding: 0,
                                  flexShrink: 0
                                }}
                                title="Supprimer ce créneau"
                              >
                                <X size={9} strokeWidth={3} />
                              </button>
                            </div>

                            {/* Bottom resize handle */}
                            <div
                              onMouseDown={(e) => handleMouseDownResize(e, tw, 'bottom', day.key)}
                              style={{
                                height: '7px',
                                cursor: 'ns-resize',
                                margin: '0 -4px -2px -4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                              title="Glisser pour modifier la fin"
                            >
                              <div style={{ width: '14px', height: '2px', background: '#4ade80', borderRadius: '1px' }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
